import { useEffect, useState } from "react";
import { RefreshCcw } from "lucide-react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

function TargetedPromoPreviewCard({ form }) {
  const [showDetails, setShowDetails] = useState(false);

  const isPercent = form.discount_type === "PERCENT";
  const maxDiscount = Number(form.max_booking_discount_amount) || 0;
  const leftText = isPercent
    ? maxDiscount > 0
      ? `Upto ₹${maxDiscount} off`
      : `Upto ${form.discount_value || 0}% off`
    : `Flat ₹${form.discount_value || 0} off`;

  const previewText = form.short_description?.trim()
    ? form.short_description.trim()
    : `Save ${isPercent ? (maxDiscount > 0 ? `₹${maxDiscount}` : `${form.discount_value || 0}%`) : `₹${form.discount_value || 0}`} + NavCash cashback on this route.`;

  return (
    <div className="w-full border rounded-2xl overflow-hidden shadow-sm bg-white">
      <div className="flex">
        <div className="bg-gray-800 text-white flex items-center justify-center px-3">
          <span className="rotate-[-90deg] text-lg font-bold whitespace-nowrap">
            {leftText}
          </span>
        </div>
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-wide">
                {(form.code || "PROMOCODE").toUpperCase()}
              </h2>
              <p className="text-gray-600 mt-2 text-sm">{previewText}</p>
              {isPercent && form.max_booking_discount_amount ? (
                <p className="text-xs text-gray-500 mt-1">
                  Max discount ₹{form.max_booking_discount_amount}
                </p>
              ) : null}
              {form.cashback_value ? (
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  +{form.cashback_type === "PERCENT"
                    ? `${form.cashback_value}%`
                    : `₹${form.cashback_value}`}{" "}
                  NavCash cashback
                </p>
              ) : null}
            </div>
            <img src="/logo.png" alt="logo" className="h-10 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-6 mt-4">
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="text-blue-600 font-semibold text-sm underline underline-offset-4"
            >
              View Details
            </button>
            <button
              type="button"
              disabled
              className="ml-auto rounded-full bg-blue-600 px-6 py-2 text-white text-sm font-semibold opacity-50 cursor-not-allowed"
            >
              Apply Code
            </button>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Promo Details</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">
              {form.description || "No description added."}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_FORM = {
  promo_code_trip_type: "FLIGHT",
  promo_code_type: "TARGETED",
  code: "",
  discount_type: "PERCENT",
  discount_value: "",
  cashback_type: "PERCENT",
  cashback_value: "",
  trip_type: "ONE_WAY",
  passenger_type: "SINGLE",
  max_usage_per_user: 1,
  max_usage_per_device: 1,
  airlines: "",
  sectors: "",
  to_destination: "",
  applicable_start_sector: 0,
  applicable_end_sector: 0,
  start_date: "",
  end_date: "",
  travel_start_date: "",
  travel_end_date: "",
  status: 1,
  description: "",
  short_description: "",
  max_booking_discount_amount: "",
  min_booking_amount: "",
};

const safeParse = (v) => {
  try {
    if (v == null) return null;
    if (Array.isArray(v) || typeof v === "object") return v;
    if (typeof v === "string") {
      const s = v.trim();
      if (!s) return null;
      if ((s.startsWith("[") && s.endsWith("]")) || (s.startsWith("{") && s.endsWith("}"))) {
        return JSON.parse(s);
      }
      return v;
    }
    return null;
  } catch {
    return v;
  }
};

const toInputDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const extractAirportCode = (text) => {
  if (!text) return "";
  let t = String(text).trim().toUpperCase().replace(/^"+|"+$/g, "");
  const match = t.match(/\(([A-Z0-9]{3,4})\)/);
  if (match?.[1]) return match[1];
  if (/^[A-Z0-9]{3,4}$/.test(t)) return t;
  return "";
};

const to01 = (v) => {
  if (Array.isArray(v) || typeof v === "object") return 0;
  if (v === 1 || v === "1" || v === true) return 1;
  return 0;
};


export default function TargetedPromoUpsertPage() {
  const navigate = useNavigate();
  const { promoId } = useParams();
  const isEdit = Boolean(promoId);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  const [airports, setAirports] = useState([]);
  const [destQuery, setDestQuery] = useState("");
  const [destResults, setDestResults] = useState([]);
  const [destOpen, setDestOpen] = useState(false);
  const [sectorFromQuery, setSectorFromQuery] = useState("");
  const [sectorFromResults, setSectorFromResults] = useState([]);
  const [sectorFromOpen, setSectorFromOpen] = useState(false);

  const [airlines, setAirlines] = useState([]);
  const [airlinesQuery, setAirlinesQuery] = useState("");
  const [airlinesResults, setAirlinesResults] = useState([]);
  const [airlinesOpen, setAirlinesOpen] = useState(false);

  useEffect(() => {
    const loadAirports = async () => {
      try {
        const res = await fetch("/airports.json");
        const json = await res.json();
        if (Array.isArray(json)) setAirports(json);
      } catch {}
    };
    const loadAirlines = async () => {
      try {
        const res = await fetch("/airlines.json");
        const json = await res.json();
        if (Array.isArray(json)) setAirlines(json);
      } catch {}
    };
    loadAirports();
    loadAirlines();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    const loadPromo = async () => {
      try {
        const res = await api.patch(`/admin/promo-codes/${promoId}`);
        const d = res.data?.data;
        const sectorsParsed = safeParse(d.sectors);
        const airlinesParsed = safeParse(d.airlines);

        const airlinesText = Array.isArray(airlinesParsed)
          ? airlinesParsed.join(",")
          : typeof airlinesParsed === "string"
          ? airlinesParsed
          : "";

        const firstSector = Array.isArray(sectorsParsed) ? sectorsParsed[0] : null;
        const sectorValue = firstSector
          ? typeof firstSector === "object"
            ? firstSector.from || ""
            : String(firstSector)
          : typeof sectorsParsed === "string"
          ? sectorsParsed
          : "";

        setForm({
          ...DEFAULT_FORM,
          promo_code_type: "TARGETED",
          promo_code_trip_type: "FLIGHT",
          code: (d.code || "").toUpperCase(),
          discount_type: d.discount_type || "PERCENT",
          discount_value: d.discount_value ?? "",
          cashback_type: d.cashback_type || "PERCENT",
          cashback_value: d.cashback_value ?? "",
          trip_type: d.trip_type || "ONE_WAY",
          passenger_type: d.passenger_type || "SINGLE",
          max_usage_per_user: d.max_usage_per_user ?? 1,
          max_usage_per_device: d.max_usage_per_device ?? 1,
          airlines: airlinesText,
          sectors: sectorValue,
          to_destination: d.to_destination || "",
          applicable_start_sector: to01(d.applicable_start_sector),
          applicable_end_sector: to01(d.applicable_end_sector),
          start_date: toInputDate(d.start_date),
          end_date: toInputDate(d.end_date),
          travel_start_date: toInputDate(d.travel_start_date),
          travel_end_date: toInputDate(d.travel_end_date),
          status: Number(d.status ?? 1),
          description: d.description ?? "",
          short_description: d.short_description ?? "",
          max_booking_discount_amount: d.max_booking_discount_amount ?? "",
          min_booking_amount: d.min_booking_amount ?? "",
        });

        setSectorFromQuery(sectorValue || "");
        setDestQuery(d.to_destination || "");
      } catch {
        toast.error("Failed to load promo");
      }
    };
    loadPromo();
  }, [promoId, isEdit]);

  // Score airport for sorting — higher score = shown first
  // Priority 1: exact airport_code match in India
  // Priority 2: city_name match in India
  // Priority 3: city_name match outside India
  // Priority 4: exact airport_code match outside India
  const getAirportScore = (a, q) => {
    const code    = (a.airport_code || "").toLowerCase();
    const city    = (a.city_name    || "").toLowerCase();
    // support both country_code:"IN" and country_name:"India"
    const isIndia = (a.country_code || "").toUpperCase() === "IN" ||
                    (a.country_name || "").toLowerCase() === "india";

    if (isIndia && code === q)         return 7;
    if (isIndia && city === q)         return 6;
    if (isIndia && city.startsWith(q)) return 5;
    if (isIndia && city.includes(q))   return 4;
    if (city === q)                    return 3;
    if (city.startsWith(q))            return 2;
    if (city.includes(q))              return 1;
    if (code === q)                    return 0;
    return -1;
  };

  useEffect(() => {
    if (!sectorFromOpen || !sectorFromQuery.trim()) { setSectorFromResults([]); return; }
    const q = sectorFromQuery.trim().toLowerCase();
    setSectorFromResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name    || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [sectorFromQuery, airports, sectorFromOpen]);

  useEffect(() => {
    if (!destOpen || !destQuery.trim()) { setDestResults([]); return; }
    const q = destQuery.trim().toLowerCase();
    setDestResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name    || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [destQuery, airports, destOpen]);

  useEffect(() => {
    if (!airlinesOpen || !airlinesQuery.trim()) {
      setAirlinesResults([]);
      return;
    }

    const q = airlinesQuery.trim().toLowerCase();
    setAirlinesResults(
      airlines
        .filter((a) => {
          const name = (a.name || "").toLowerCase();
          const iata = (a.iata || "").toLowerCase();
          return name.includes(q) || iata.includes(q);
        })
        .slice(0, 10)
    );
  }, [airlinesQuery, airlines, airlinesOpen]);

  const onChange = (k) => (e) =>
    setForm((s) => ({
      ...s,
      [k]: k === "code" ? (e.target.value || "").toUpperCase() : e.target.value,
    }));

  const generateCode = async () => {
    try {
      setGenLoading(true);
      const res = await api.get("/admin/promo-codes/generate");
      const code = res.data?.data?.code;
      if (!code) throw new Error("Code missing from API");
      setForm((s) => ({ ...s, code: String(code).toUpperCase() }));
      toast.success("Promo code generated");
    } catch {
      toast.error("Failed to generate code");
    } finally {
      setGenLoading(false);
    }
  };

  const uploadPdf = async (id) => {
    if (!pdfFile) return;
    const fd = new FormData();
    fd.append("pdf", pdfFile);
    setPdfUploading(true);
    try {
      await api.post(`/admin/promo-codes/${id}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF uploaded");
    } catch {
      toast.error("PDF upload failed");
    } finally {
      setPdfUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || pdfUploading) return;

    if (!form.discount_value || Number(form.discount_value) <= 0) {
      toast.error("Discount value is required");
      return;
    }

    const fromCode = String(form.sectors || "").trim().toUpperCase();
    const sectorsPayload = fromCode ? [fromCode] : null;
    const toDestPayload = String(form.to_destination || "").trim() || null;

    const payload = {
      promo_code_trip_type: "FLIGHT",
      promo_code_type: "TARGETED",
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      cashback_type: form.cashback_type,
      cashback_value: form.cashback_value !== "" ? Number(form.cashback_value) : null,
      trip_type: form.trip_type,
      passenger_type: form.passenger_type,
      max_usage_per_user: Number(form.max_usage_per_user) || 1,
      max_usage_per_device: Number(form.max_usage_per_device) || 1,
      airlines: (form.airlines || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
      sectors: sectorsPayload,
      to_destination: toDestPayload,
      applicable_start_sector: 0,
      applicable_end_sector: 0,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      travel_start_date: form.travel_start_date ? new Date(form.travel_start_date).toISOString() : null,
      travel_end_date: form.travel_end_date ? new Date(form.travel_end_date).toISOString() : null,
      min_booking_amount: form.min_booking_amount === "" ? null : Number(form.min_booking_amount),
      description: (form.description || "").trim() || null,
      short_description: (form.short_description || "").trim() || null,
      max_booking_discount_amount:
        form.discount_type === "PERCENT" && form.max_booking_discount_amount !== ""
          ? Number(form.max_booking_discount_amount)
          : null,
    };

    if (isEdit) payload.status = Number(form.status);

    setSaving(true);
    try {
      let createdId = promoId;
      if (!isEdit) {
        const res = await api.post("/admin/promo-codes", payload);
        createdId = res?.data?.promoCodeId || res?.data?.data?.promo_id || res?.data?.data?.id;
        toast.success("Targeted promo created");
      } else {
        await api.patch(`/admin/promo-codes/${promoId}`, payload);
        toast.success("Targeted promo updated");
      }
      if (pdfFile && createdId) await uploadPdf(createdId);
      navigate("/admin/targeted-promo-codes");
    } catch (e2) {
      toast.error(e2?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">
            {isEdit ? "Edit Targeted Promo" : "Create Targeted Promo"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Instant discount + NavCash cashback, both apply together
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/targeted-promo-codes")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start sm:self-auto"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Code */}
          <div>
            <label className="text-sm font-semibold">Code</label>
            <div className="relative mt-2">
              <input
                className="w-full border rounded-xl px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-black disabled:bg-gray-50"
                value={form.code}
                onChange={onChange("code")}
                disabled={isEdit}
                required
              />
              {!isEdit && (
                <button
                  type="button"
                  onClick={generateCode}
                  disabled={genLoading || saving || pdfUploading}
                  title="Generate promo code"
                  className="absolute inset-y-0 right-2 flex items-center justify-center text-gray-500 hover:text-black disabled:opacity-50"
                >
                  <RefreshCcw size={18} className={genLoading ? "animate-spin" : ""} />
                </button>
              )}
            </div>
          </div>

          {/* Short Description */}
          <div>
            <label className="text-sm font-semibold">Short Description</label>
            <input
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.short_description}
              onChange={onChange("short_description")}
              placeholder="Shown in preview"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-semibold">Description</label>
            <textarea
              className="mt-2 w-full border rounded-xl px-3 py-2 min-h-[120px] resize-none"
              value={form.description}
              onChange={onChange("description")}
              placeholder="Write full promo description..."
            />
          </div>

          {/* Discount — always shown for TARGETED */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Discount Type</label>
              <select
                className="mt-2 w-full border rounded-xl px-3 py-2"
                value={form.discount_type}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    discount_type: e.target.value,
                    max_booking_discount_amount:
                      e.target.value === "PERCENT" ? s.max_booking_discount_amount : "",
                  }))
                }
              >
                <option value="PERCENT">PERCENT</option>
                <option value="FLAT">FLAT</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Discount Value</label>
              <input
                type="number"
                min={1}
                required
                className="mt-2 w-full border rounded-xl px-3 py-2"
                value={form.discount_value}
                onChange={onChange("discount_value")}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">
              Max Booking Discount Amount (Percent only)
            </label>
            <input
              type="number"
              min={1}
              disabled={form.discount_type !== "PERCENT"}
              className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50"
              value={form.max_booking_discount_amount}
              onChange={onChange("max_booking_discount_amount")}
            />
          </div>

          {/* NavCash Cashback — always shown for TARGETED */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border rounded-xl bg-blue-50">
            <div className="col-span-2">
              <p className="text-sm font-semibold text-blue-700">NavCash Cashback</p>
            </div>
            <div>
              <label className="text-sm font-semibold">Cashback Type</label>
              <select
                className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                value={form.cashback_type}
                onChange={onChange("cashback_type")}
              >
                <option value="PERCENT">PERCENT</option>
                <option value="FLAT">FLAT</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold">Cashback Value</label>
              <input
                type="number"
                min={0}
                className="mt-2 w-full border rounded-xl px-3 py-2 bg-white"
                value={form.cashback_value}
                onChange={onChange("cashback_value")}
              />
            </div>
          </div>

          {/* Usage limits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Max Usage/User</label>
              <input
                type="number"
                min={1}
                className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50"
                value={form.max_usage_per_user}
                onChange={onChange("max_usage_per_user")}
                disabled={isEdit}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">Max Usage/Device</label>
              <input
                type="number"
                min={1}
                className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50"
                value={form.max_usage_per_device}
                onChange={onChange("max_usage_per_device")}
                disabled={isEdit}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Min Booking Amount</label>
            <input
              type="number"
              min={0}
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.min_booking_amount}
              onChange={onChange("min_booking_amount")}
            />
          </div>

          {/* Sectors (Flight only) */}
          <div>
            <label className="text-sm font-semibold">Sectors</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div className="relative">
                <label className="text-xs font-medium">From sector</label>
                <input
                  disabled={false}
                  className="mt-2 w-full border rounded-xl px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  value={sectorFromQuery}
                  placeholder="Type code or choose..."
                  onFocus={() => setSectorFromOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setSectorFromOpen(false), 150);
                    const code = extractAirportCode(sectorFromQuery);
                    setForm((s) => ({ ...s, sectors: code }));
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSectorFromQuery(val);
                    setSectorFromOpen(true);
                    setForm((s) => ({ ...s, sectors: extractAirportCode(val) }));
                  }}
                />
                {sectorFromOpen && sectorFromResults.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                    {sectorFromResults.map((a) => (
                      <button
                        key={a.airport_code}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSectorFromQuery(`${a.airport_name} (${a.airport_code})`);
                          setForm((s) => ({ ...s, sectors: String(a.airport_code) }));
                          setSectorFromOpen(false);
                        }}
                      >
                        {a.airport_name} ({a.airport_code})
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className="text-xs font-medium">To destination</label>
                <input
                  disabled={false}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black disabled:bg-gray-50 disabled:cursor-not-allowed"
                  value={destQuery}
                  placeholder="Type code or choose..."
                  onFocus={() => setDestOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setDestOpen(false), 150);
                    setForm((s) => ({ ...s, to_destination: extractAirportCode(destQuery) }));
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDestQuery(val);
                    setDestOpen(true);
                    setForm((s) => ({ ...s, to_destination: extractAirportCode(val) }));
                  }}
                />
                {destOpen && destResults.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                    {destResults.map((a) => (
                      <button
                        key={a.airport_code}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setDestQuery(`${a.airport_name} (${a.airport_code})`);
                          setForm((s) => ({ ...s, to_destination: String(a.airport_code) }));
                          setDestOpen(false);
                        }}
                      >
                        {a.airport_name} ({a.airport_code})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {form.sectors ? (
              <p className="text-xs text-gray-500 mt-2">Stored sector: {form.sectors}</p>
            ) : null}
          </div>

          <div className="relative">
            <label className="text-sm font-semibold">Airlines (comma)</label>
            <input
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
              value={airlinesQuery}
              placeholder="Type airline code or name..."
              onFocus={() => setAirlinesOpen(true)}
              onBlur={() => {
                setTimeout(() => setAirlinesOpen(false), 150);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setAirlinesQuery(val);
                setAirlinesOpen(true);
              }}
            />
            {airlinesOpen && airlinesResults.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                {airlinesResults.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const currentList = (form.airlines || "")
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean);
                      if (!currentList.includes(a.iata)) {
                        const newList = [...currentList, a.iata].join(",");
                        setForm((s) => ({ ...s, airlines: newList }));
                      }
                      setAirlinesQuery("");
                      setAirlinesOpen(false);
                    }}
                  >
                    {a.iata ? `${a.iata} - ${a.name}` : a.name}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2">
              {form.airlines && (
                <div className="flex flex-wrap gap-2">
                  {(form.airlines || "")
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean)
                    .map((code) => (
                      <span
                        key={code}
                        className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {code}
                        <button
                          type="button"
                          onClick={() => {
                            const newList = (form.airlines || "")
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean)
                              .filter((x) => x !== code)
                              .join(",");
                            setForm((s) => ({ ...s, airlines: newList }));
                          }}
                          className="text-gray-500 hover:text-red-600 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Trip Type */}
          <div>
            <label className="text-sm font-semibold">Trip Type</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.trip_type}
              onChange={onChange("trip_type")}
            >
              <option value="ONE_WAY">ONE WAY</option>
              <option value="RETURN">RETURN</option>
              <option value="ANY">ANY</option>
            </select>
          </div>

          {/* Passenger Type */}
          <div>
            <label className="text-sm font-semibold">Passenger Type</label>
            <select
              className="mt-2 w-full border rounded-xl px-3 py-2"
              value={form.passenger_type}
              onChange={onChange("passenger_type")}
            >
              <option value="SINGLE">SINGLE</option>
              <option value="MULTIPLE">MULTIPLE</option>
              <option value="ANY">ANY</option>
            </select>
          </div>

          {/* Dates */}
          {/* <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Start Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.start_date} onChange={onChange("start_date")} />
            </div>
            <div>
              <label className="text-sm font-semibold">End Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.end_date} onChange={onChange("end_date")} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Travel Start Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.travel_start_date} onChange={onChange("travel_start_date")} />
            </div>
            <div>
              <label className="text-sm font-semibold">Travel End Date</label>
              <input type="date" className="mt-2 w-full border rounded-xl px-3 py-2" value={form.travel_end_date} onChange={onChange("travel_end_date")} />
            </div>
          </div> */}

          {isEdit && (
            <div>
              <label className="text-sm font-semibold">Status</label>
              <select className="mt-2 w-full border rounded-xl px-3 py-2" value={form.status} onChange={onChange("status")}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          )}

          {/* <div>
            <label className="text-sm font-semibold">Promo PDF (optional)</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className="mt-2 w-full"
            />
          </div> */}

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate("/admin/targeted-promo-codes")}
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              disabled={saving || pdfUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || pdfUploading}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving || pdfUploading ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>

        <div>
          <h2 className="text-lg font-bold mb-3">Preview</h2>
          <TargetedPromoPreviewCard form={form} />
        </div>
      </div>
    </div>
  );
}
